import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { Person } from '../models/person.model';
import { PersonService, TransferPersonCommunityRequest } from '../services/person.service';

export interface CommunityTransferDialogData {
  person: Person;
  initialDate?: string | null;
  lockDate?: boolean;
  minDate?: Date | null;
}

export interface CommunityTransferDialogResult extends TransferPersonCommunityRequest {
  targetUnitName: string;
}

@Component({
  selector: 'app-community-transfer-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    DatePickerComponent,
    ModalShellComponent,
    UnitSelectorComponent,
  ],
  templateUrl: './community-transfer-dialog.component.html',
  styleUrl: './community-transfer-dialog.component.scss',
})
export class CommunityTransferDialogComponent implements OnDestroy {
  readonly modalConfig: ModalShellConfig = { title: 'Pase de comunidad', size: 'lg', layout: 'form' };
  readonly form: FormGroup;
  readonly minDate: any | null;

  allCommunities: OrganizationUnitItem[] = [];
  selectedCommunity: OrganizationUnitItem | null = null;
  isLoadingCommunities = true;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly cdr: ChangeDetectorRef,
    private readonly personService: PersonService,
    private readonly dialogRef: MatDialogRef<CommunityTransferDialogComponent, CommunityTransferDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: CommunityTransferDialogData,
  ) {
    this.minDate = data.minDate ? this.fromDate(data.minDate) : null;
    this.form = this.fb.group({
      targetUnitId: [null as string | null, Validators.required],
      effectiveDate: [{ value: this.fromIsoDate(data.initialDate), disabled: data.lockDate === true }, [Validators.required, this.notBeforeMinDateValidator()]],
      reason: [null as string | null, Validators.maxLength(300)],
    });

    this.personService.getCommunityTransferTargets(this.data.person.id)
      .pipe(
        finalize(() => {
          this.isLoadingCommunities = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(units => {
        this.allCommunities = units
          .filter(unit => unit.levelId === 5)
          .filter(unit => unit.id !== this.data.person.communityId);
        this.cdr.markForCheck();
      });
  }

  onCommunitySelected(unitId: string): void {
    this.selectedCommunity = this.allCommunities.find(unit => unit.id === unitId) ?? null;
  }

  onConfirm(): void {
    const targetUnitId = this.form.get('targetUnitId')!.value;
    const targetUnit = this.selectedCommunity ?? this.allCommunities.find(unit => unit.id === targetUnitId) ?? null;

    if (this.form.invalid || !targetUnit?.id) {
      this.form.markAllAsTouched();
      return;
    }

    const effectiveDate = this.toIsoDate(this.form.get('effectiveDate')!.value!);

    this.dialogRef.close({
      targetUnitId: targetUnit.id,
      targetUnitName: targetUnit.name,
      effectiveDate,
      reason: this.form.get('reason')!.value || null,
    });
  }

  get movesMinistryActivityWithCommunity(): boolean {
    const ministry = this.data.person.ministry;
    if (ministry?.status !== 'Active' || !this.data.person.communityId) {
      return false;
    }

    return ministry.activities?.some(activity =>
      activity.unitId === this.data.person.communityId && !activity.validTo)
      ?? ministry.unitId === this.data.person.communityId;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private fromIsoDate(value?: string | null): any {
    if (!value) {
      return this.dateAdapter.today();
    }

    const [year, month, day] = value.split('-').map(Number);
    return this.dateAdapter.createDate(year, month - 1, day);
  }

  private fromDate(value: Date): any {
    return this.dateAdapter.createDate(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private notBeforeMinDateValidator(): (control: AbstractControl) => ValidationErrors | null {
    return control => {
      if (!this.minDate || !control.value) {
        return null;
      }

      return this.dateAdapter.compareDate(control.value, this.minDate) < 0 ? { beforeBirthDate: true } : null;
    };
  }
}
